// ============================================================================
// VehiculoListInput — captura UNA o VARIAS unidades (flota) al EMITIR una póliza.
// Se usa en el formulario de "Nueva Póliza" y en la solicitud. Después de emitida,
// las unidades se agregan/quitan/cambian por ENDOSO (no aquí).
// value: array de vehículos; onChange(nextArray).
// ============================================================================
import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import CoberturaMiniList from './CoberturaMiniList';

const TIPOS_VEHICULO = ['Sedán', 'SUV', 'Pickup', 'Van', 'Camión', 'Motocicleta', 'Otro'];

export const EMPTY_VEH = {
  anio: '', marca: '', modelo: '', color: '', tipo_vehiculo: '',
  pasajeros: '', no_motor: '', chasis: '', chasis_igual_motor: true,
  placa: '', placa_por_reportar: false, coberturas: [],
  dueno_igual: true, dueno_nombre: '', dueno_cedula: '',
};

// Devuelve un array de mensajes de lo que falta (para validar al crear).
export function validarVehiculos(list) {
  const faltan = [];
  (list || []).forEach((v, i) => {
    const n = (list.length > 1) ? ` (vehículo ${i + 1})` : '';
    if (!v.anio) faltan.push(`Año${n}`);
    if (!v.marca) faltan.push(`Marca${n}`);
    if (!v.modelo) faltan.push(`Modelo${n}`);
    if (!v.no_motor) faltan.push(`VIN / N° de motor${n}`);
  });
  return faltan;
}

export default function VehiculoListInput({ value, onChange, onFieldBlur }) {
  const list = (value && value.length) ? value : [{ ...EMPTY_VEH }];
  const blur = (campo, valor) => { if (onFieldBlur) onFieldBlur(campo, (valor || '').trim()); };
  const update = (idx, k, v) => onChange(list.map((x, i) => i === idx ? { ...x, [k]: v } : x));
  const add = () => onChange([...list, { ...EMPTY_VEH }]);
  const remove = (idx) => onChange(list.length > 1 ? list.filter((_, i) => i !== idx) : list);

  return (
    <div>
      {list.map((veh, idx) => (
        <div key={idx} style={{
          background: 'rgba(var(--steel-rgb),0.18)', border: '1px solid rgba(var(--silver-rgb),0.12)',
          borderRadius: 8, padding: '12px 14px', marginBottom: 10,
        }}>
          {list.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sky-light)' }}>Vehículo {idx + 1}</div>
              <button type="button" className="btn btn-icon btn-danger btn-sm" onClick={() => remove(idx)} title="Quitar este vehículo">
                <Trash2 size={13} />
              </button>
            </div>
          )}
          <div className="form-grid form-grid-3">
            <div className="form-group"><label className="form-label">Año *</label>
              <input className="form-input" type="number" value={veh.anio} onChange={e => update(idx, 'anio', e.target.value)} placeholder="2024" /></div>
            <div className="form-group"><label className="form-label">Marca *</label>
              <input className="form-input" value={veh.marca} onChange={e => update(idx, 'marca', e.target.value)} placeholder="Toyota" /></div>
            <div className="form-group"><label className="form-label">Modelo *</label>
              <input className="form-input" value={veh.modelo} onChange={e => update(idx, 'modelo', e.target.value)} placeholder="Corolla" /></div>
            <div className="form-group"><label className="form-label">Color</label>
              <input className="form-input" value={veh.color} onChange={e => update(idx, 'color', e.target.value)} placeholder="Blanco" /></div>
            <div className="form-group"><label className="form-label">Tipo</label>
              <select className="form-select" value={veh.tipo_vehiculo} onChange={e => update(idx, 'tipo_vehiculo', e.target.value)}>
                <option value="">—</option>
                {TIPOS_VEHICULO.map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Pasajeros</label>
              <input className="form-input" type="number" value={veh.pasajeros} onChange={e => update(idx, 'pasajeros', e.target.value)} placeholder="5" /></div>
            <div className="form-group"><label className="form-label">VIN / N° Motor *</label>
              <input className="form-input" value={veh.no_motor} onChange={e => update(idx, 'no_motor', e.target.value)}
                onBlur={e => blur('no_motor', e.target.value)} /></div>
            <div className="form-group">
              <label className="form-label">Chasis</label>
              <input className="form-input" value={veh.chasis_igual_motor ? veh.no_motor : veh.chasis}
                disabled={!!veh.chasis_igual_motor} onChange={e => update(idx, 'chasis', e.target.value)}
                onBlur={e => blur('chasis', e.target.value)} placeholder="Número de chasis" />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
              <input type="checkbox" id={`veh-chasis-${idx}`} checked={!!veh.chasis_igual_motor}
                onChange={e => update(idx, 'chasis_igual_motor', e.target.checked)} style={{ accentColor: 'var(--sky)', width: 14, height: 14, cursor: 'pointer' }} />
              <label htmlFor={`veh-chasis-${idx}`} style={{ fontSize: 12, color: 'var(--silver)' }}>Chasis = Motor</label>
            </div>
            <div className="form-group"><label className="form-label">Placa</label>
              <input className="form-input" value={veh.placa_por_reportar ? '' : veh.placa}
                disabled={!!veh.placa_por_reportar} placeholder={veh.placa_por_reportar ? 'Por reportar' : 'AAA-000'}
                onChange={e => update(idx, 'placa', e.target.value)} onBlur={e => blur('placa', e.target.value)} /></div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
              <input type="checkbox" id={`veh-placa-${idx}`} checked={!!veh.placa_por_reportar}
                onChange={e => update(idx, 'placa_por_reportar', e.target.checked)} style={{ accentColor: 'var(--sky)', width: 14, height: 14, cursor: 'pointer' }} />
              <label htmlFor={`veh-placa-${idx}`} style={{ fontSize: 12, color: 'var(--silver)' }}>Por reportar</label>
            </div>
          </div>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(var(--silver-rgb),0.15)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
              <input type="checkbox" checked={veh.dueno_igual !== false}
                onChange={e => update(idx, 'dueno_igual', e.target.checked)} style={{ accentColor: 'var(--sky)', width: 15, height: 15 }} />
              El dueño de esta unidad es el <span style={{ color: 'var(--sky-light)' }}>contratante de la póliza</span>
            </label>
            {veh.dueno_igual === false && (
              <div className="form-grid form-grid-2" style={{ marginTop: 8 }}>
                <div className="form-group"><label className="form-label">Propietario (según RUV)</label>
                  <input className="form-input" value={veh.dueno_nombre || ''} onChange={e => update(idx, 'dueno_nombre', e.target.value)} placeholder="Nombre del dueño del vehículo" /></div>
                <div className="form-group"><label className="form-label">Cédula / RUC del propietario</label>
                  <input className="form-input" value={veh.dueno_cedula || ''} onChange={e => update(idx, 'dueno_cedula', e.target.value)} placeholder="8-888-8888" /></div>
              </div>
            )}
          </div>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--silver)', letterSpacing: 0.5, marginBottom: 6 }}>COBERTURAS DE ESTA UNIDAD</div>
            <CoberturaMiniList value={veh.coberturas || []} onChange={cobs => update(idx, 'coberturas', cobs)} />
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-secondary btn-sm" onClick={add}>
        <Plus size={13} /> Agregar vehículo
      </button>
    </div>
  );
}
